// The Work-section SHELL CONTAINER (brief §5.1: landing → board → card, plus the operator Composer). The
// shell mounts this ONE component for the `work` surface; it owns the in-section `WorkRoute` state (landing
// default) and wires the callbacks-only navigation the surfaces declare (surface-contracts.ts) — the surfaces
// stay shell-agnostic. The `WorkApiProvider` is supplied by the App root (App.tsx), so this container only
// routes; it never provides the transport. Role arrives as a PROP the shell resolves; verdict controls still
// render only where the projection's `canRule` is true — rendering only, the owning module is the boundary
// (G-180). This mirrors the proven dev-harness RoutedSurface (dev/WorkHarnessApp.tsx), minus the harness chrome.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DashboardRole } from '../../app/surfaces.ts';
import { useShell } from '../../app/ShellContext.tsx';
import type { WorkRoute } from './surface-contracts.ts';
import { WorkLandingSurface } from './landing/WorkLandingSurface.tsx';
import { WorkBoardSurface } from './board/WorkBoardSurface.tsx';
import { WorkCardSurface } from './card/WorkCardSurface.tsx';
import { WorkComposerSurface } from './composer/WorkComposerSurface.tsx';

const LANDING_ROUTE: WorkRoute = { kind: 'landing' };

/** Render the surface for the current in-section route, wiring the contract callbacks. */
function RoutedWorkSurface({
  route,
  role,
  nav,
}: {
  readonly route: WorkRoute;
  readonly role: DashboardRole;
  readonly nav: {
    readonly openBoard: (definitionId: string) => void;
    readonly openCard: (itemId: string) => void;
    readonly openComposer: () => void;
    readonly requestWorkflow: () => void;
    readonly back: () => void;
  };
}): React.JSX.Element {
  switch (route.kind) {
    case 'board':
      return (
        <WorkBoardSurface
          role={role}
          definitionId={route.definitionId}
          onOpenCard={nav.openCard}
          onBack={nav.back}
        />
      );
    case 'card':
      return <WorkCardSurface role={role} itemId={route.itemId} onBack={nav.back} />;
    case 'composer':
      return <WorkComposerSurface role={role} onBack={nav.back} onOpenBoard={nav.openBoard} />;
    default:
      return (
        <WorkLandingSurface
          role={role}
          onOpenBoard={nav.openBoard}
          onOpenComposer={nav.openComposer}
          onRequestWorkflow={nav.requestWorkflow}
        />
      );
  }
}

/** The shell-mounted Work section: a small in-section router over the four Work surfaces. */
export function WorkSectionContainer({
  role = 'admin_md',
}: {
  readonly role?: DashboardRole;
}): React.JSX.Element {
  const { openNewChatWithDraft } = useShell();
  const [stack, setStack] = useState<readonly WorkRoute[]>([LANDING_ROUTE]);
  const route = stack[stack.length - 1] ?? LANDING_ROUTE;

  const push = useCallback((next: WorkRoute) => setStack((s) => [...s, next]), []);
  const nav = useMemo(
    () => ({
      openBoard: (definitionId: string) => push({ kind: 'board', definitionId }),
      openCard: (itemId: string) => push({ kind: 'card', itemId }),
      openComposer: () => push({ kind: 'composer' }),
      requestWorkflow: () =>
        openNewChatWithDraft(
          'Request a new workflow. What I need it to handle, in plain English: ',
        ),
      back: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    }),
    [openNewChatWithDraft, push],
  );

  // The Composer is the operator console: when the shell switches AWAY from operator, drop any composer route
  // (mirrors the shell's own role fallback — a surface the new role cannot reach returns to the section home).
  useEffect(() => {
    if (role !== 'operator') {
      setStack((s) => (s.some((entry) => entry.kind === 'composer') ? [LANDING_ROUTE] : s));
    }
  }, [role]);

  return (
    <div data-work-route={route.kind}>
      <RoutedWorkSurface route={route} role={role} nav={nav} />
    </div>
  );
}
